import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { previewAudience, createAudience, getWarehouses, uploadAudienceUsers } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';

const AudienceBuilder = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateParam = searchParams.get('template');

  const [name, setName] = useState('');
  const [type, setType] = useState('static'); // static | dynamic
  const [filters, setFilters] = useState([
    { type: 'order_count', mode: 'include', params: { min: 1, max: null }, combinator: 'and' },
  ]);

  // A/B Split config
  const [enableSplit, setEnableSplit] = useState(false);
  const [splitPct, setSplitPct] = useState(80); // 80% treatment, 20% holdout
  const [saving, setSaving] = useState(false);

  // Preview state
  const [previewCount, setPreviewCount] = useState(0);
  const [sampleUsers, setSampleUsers] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // CSV Modal state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvText, setCsvText] = useState('');

  // File upload state
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // All-users confirmation modal state
  const [showAllUsersModal, setShowAllUsersModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  // Hub list
  const [warehouses, setWarehouses] = useState([]);

  // Fetch warehouse list from API
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await getWarehouses();
        if (res && res.data && Array.isArray(res.data)) {
          setWarehouses(res.data.map((w) => ({ id: w.id, name: w.name })));
        }
      } catch (err) {
        console.error("Failed to load warehouses:", err);
      }
    };
    fetchWarehouses();
  }, []);

  // Handle template pre-filling
  useEffect(() => {
    if (templateParam) {
      if (templateParam === 'new_signups') {
        setName('New Signups (0 Orders)');
        setType('dynamic');
        setFilters([{ type: 'order_count', mode: 'include', params: { min: 0, max: 0 } }]);
      } else if (templateParam === 'lapsed_45d') {
        setName('Lapsed Customers (45+ Days)');
        setType('static');
        setFilters([{ type: 'last_order_date', mode: 'include', params: { older_than_days: 45 } }]);
      } else if (templateParam === 'cross_sell_inverter') {
        setName('Inverter Cross-Sell (Panels Bought, No Inverter)');
        setType('static');
        setFilters([
          { type: 'ordered_category', mode: 'include', params: { values: ['Solar Panels'] } },
          { type: 'ordered_category', mode: 'exclude', params: { values: ['Solar Inverter'] } },
        ]);
      } else if (templateParam === 'new_installs') {
        const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
        setName('New Installs This Month');
        setType('dynamic');
        setFilters([{ type: 'signup_date', mode: 'include', params: { from: firstOfMonth, to: null } }]);
      }
    }
  }, [templateParam]);

  // Debounced preview trigger
  const runPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await previewAudience(filters);
      if (res && res.data) {
        setPreviewCount(res.data.count || 0);
        setSampleUsers(res.data.samples || []);
      } else {
        setPreviewCount(0);
        setSampleUsers([]);
      }
    } catch (err) {
      setPreviewCount(0);
      setSampleUsers([]);
    } finally {
      setPreviewLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      runPreview();
    }, 500);
    return () => clearTimeout(timer);
  }, [filters, runPreview]);

  const addFilterRow = () => {
    const allTypes = ['order_count', 'first_order_date', 'last_order_date', 'ordered_skus', 'ordered_category', 'warehouse', 'last_active', 'signup_date', 'specific_users', 'audience'];
    const usedTypes = filters.map((f) => f.type);
    const nextType = allTypes.find((t) => !usedTypes.includes(t)) || 'order_count';
    const defaultParams = {
      order_count: { min: 1, max: null },
      first_order_date: { from: null, to: null },
      last_order_date: { older_than_days: 45 },
      ordered_skus: { values: [] },
      ordered_category: { values: [] },
      warehouse: { ids: [] },
      last_active: { within_days: 30 },
      signup_date: { from: null, to: null },
      specific_users: { values: [] },
      audience: { audience_ids: [] },
    };
    setFilters([...filters, { type: nextType, mode: 'include', params: defaultParams[nextType] || {}, combinator: 'and' }]);
  };

  const removeFilterRow = (index) => {
    const next = [...filters];
    next.splice(index, 1);
    setFilters(next);
  };

  const toggleCombinator = (index) => {
    const next = [...filters];
    next[index].combinator = next[index].combinator === 'or' ? 'and' : 'or';
    setFilters(next);
  };

  const updateFilterRow = (index, field, value) => {
    const next = [...filters];
    if (field === 'mode') {
      next[index].mode = value;
    } else if (field === 'type') {
      next[index].type = value;
      next[index].params = {}; // reset params for new type
    } else if (field === 'params') {
      next[index].params = { ...next[index].params, ...value };
    }
    setFilters(next);
  };

  const handleSave = async (overridePayload) => {
    if (saving) return;
    const payload = overridePayload || {
      name: name.trim(),
      type,
      filters,
      split_pct: enableSplit && type === 'static' ? splitPct : null,
    };

    if (!payload.name) {
      toast.error('Please enter an Audience Name');
      return;
    }

    setSaving(true);
    try {
      toast.info('Creating audience...');
      await createAudience(payload);
      toast.success('Audience created successfully!');
      navigate('/audiences');
    } catch (err) {
      if (err.requires_confirmation) {
        // Backend says zero-filter audience requires confirmation
        setPendingPayload(payload);
        setShowAllUsersModal(true);
      } else {
        toast.error(err.message || 'Failed to create audience');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmAllUsers = async () => {
    setShowAllUsersModal(false);
    if (!pendingPayload) return;
    try {
      toast.info('Creating audience (confirmed all users)...');
      await createAudience({ ...pendingPayload, confirm_all_users: true });
      toast.success('Audience created successfully!');
      navigate('/audiences');
    } catch (err) {
      toast.error(err.message || 'Failed to create audience');
    } finally {
      setPendingPayload(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 5MB.');
      e.target.value = '';
      return;
    }

    const validTypes = ['.csv', '.xlsx', '.xls'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validTypes.includes(ext)) {
      toast.error('Invalid file type. Accepted: .csv, .xlsx, .xls');
      e.target.value = '';
      return;
    }

    setUploadLoading(true);
    setUploadResult(null);
    try {
      const res = await uploadAudienceUsers(file);
      const data = res.data || res;
      const values = data.values || [];
      const totalParsed = data.total_parsed || values.length;
      const matchedUsers = data.matched_users || values.length;
      const skipped = data.skipped || 0;

      setUploadResult({ totalParsed, matchedUsers, values });

      // Auto-populate the specific_users filter
      const existingIdx = filters.findIndex((f) => f.type === 'specific_users');
      if (existingIdx >= 0) {
        const next = [...filters];
        next[existingIdx].params = { values };
        setFilters(next);
      } else {
        setFilters([...filters, { type: 'specific_users', mode: 'include', params: { values }, combinator: 'and' }]);
      }

      toast.success(`${totalParsed} user IDs parsed, ${matchedUsers} matched to users`);
      if (skipped > 0) {
        toast.warn(`${skipped} row(s) skipped — not valid user IDs (phone numbers are no longer accepted)`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
    } finally {
      setUploadLoading(false);
      e.target.value = '';
    }
  };

  const handleCsvSubmit = () => {
    // Accept user IDs only — one per line. Keep valid positive integers, dedupe.
    const ids = Array.from(
      new Set(
        csvText
          .split(/[\n,]/)
          .map((l) => parseInt(l.trim(), 10))
          .filter((n) => Number.isInteger(n) && n > 0)
      )
    );
    if (ids.length === 0) {
      toast.error('No valid user IDs detected');
      return;
    }
    const existingIdx = filters.findIndex((f) => f.type === 'specific_users');
    if (existingIdx >= 0) {
      const next = [...filters];
      next[existingIdx].params = { values: ids };
      setFilters(next);
    } else {
      setFilters([
        ...filters,
        { type: 'specific_users', mode: 'include', params: { values: ids }, combinator: 'and' },
      ]);
    }
    setShowCsvModal(false);
    setCsvText('');
    toast.success(`Added ${ids.length} user IDs to audience filter`);
  };

  // Treatment / Holdout member counts
  const treatmentCount = Math.round(previewCount * (splitPct / 100));
  const holdoutCount = previewCount - treatmentCount;

  return (
    <div className="container-fluid" style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      
      {/* Top Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <button
            onClick={() => navigate('/audiences')}
            style={{ border: 'none', background: 'none', color: '#2563eb', fontWeight: '600', cursor: 'pointer', fontSize: '13px', padding: 0, marginBottom: '4px' }}
          >
            ← Back to Audiences
          </button>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
            Create New Audience
          </h2>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => navigate('/audiences')}
            className="btn btn-outline-secondary"
            style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
          >
            Cancel
          </button>

          <button
            onClick={() => handleSave()}
            disabled={saving}
            className="btn btn-primary"
            style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 24px',
              fontWeight: '600',
              fontSize: '13px',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save Audience'}
          </button>
        </div>
      </div>

      <div className="row">
        
        {/* Left Column — Filter Builder */}
        <div className="col-lg-8">
          
          {/* Card 1: Basic Info */}
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>
              1. Audience Details & Evaluation Type
            </h4>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                Audience Name *
              </label>
              <input
                type="text"
                placeholder="e.g. Lapsed 45d - Lucknow Hub"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
              />
            </div>

            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px', display: 'block' }}>
                Membership Evaluation Type
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div
                  onClick={() => setType('static')}
                  style={{
                    border: type === 'static' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    background: type === 'static' ? '#eff6ff' : '#ffffff',
                    borderRadius: '10px',
                    padding: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ fontWeight: '700', color: type === 'static' ? '#1d4ed8' : '#0f172a', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>Static Audience</span>
                    <span style={{ fontSize: '10px', background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: '10px' }}>For A/B Tests</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    Membership is snapshotted (frozen list). Ideal for A/B testing so groups never drift. Can be manually refreshed.
                  </div>
                </div>

                <div
                  onClick={() => {
                    setType('dynamic');
                    setEnableSplit(false);
                  }}
                  style={{
                    border: type === 'dynamic' ? '2px solid #d97706' : '1px solid #cbd5e1',
                    background: type === 'dynamic' ? '#fffbeb' : '#ffffff',
                    borderRadius: '10px',
                    padding: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ fontWeight: '700', color: type === 'dynamic' ? '#b45309' : '#0f172a', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>Dynamic Audience</span>
                    <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '10px' }}>Evergreen</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    Filters are re-evaluated live on every campaign send or coupon apply. Always targets latest active users.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Human Sentence Filter Builder */}
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                2. Filter Builder
                <span style={{ fontSize: '11px', fontWeight: '500', color: '#64748b', marginLeft: '8px' }}>
                  Click AND/OR between rows to change logic · Values within a row are OR
                </span>
              </h4>

              <button
                onClick={() => setShowCsvModal(true)}
                className="btn btn-sm btn-outline-primary"
                style={{ fontSize: '12px', borderRadius: '6px', fontWeight: '600' }}
              >
                + Upload CSV / Paste User IDs
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {filters.map((flt, idx) => (
                <React.Fragment key={idx}>
                  {/* AND/OR combinator toggle between rows */}
                  {idx > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 0' }}>
                      <button
                        type="button"
                        onClick={() => toggleCombinator(idx)}
                        style={{
                          border: '1px solid',
                          borderColor: flt.combinator === 'or' ? '#f59e0b' : '#2563eb',
                          background: flt.combinator === 'or' ? '#fffbeb' : '#eff6ff',
                          color: flt.combinator === 'or' ? '#b45309' : '#1d4ed8',
                          padding: '3px 14px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          letterSpacing: '0.5px',
                        }}
                        title="Click to toggle between AND / OR"
                      >
                        {flt.combinator === 'or' ? 'OR' : 'AND'}
                      </button>
                    </div>
                  )}
                <div
                  style={{
                    background: flt.mode === 'include' ? '#f8fafc' : '#fff5f5',
                    border: flt.mode === 'include' ? '1px solid #cbd5e1' : '1px solid #fecaca',
                    borderRadius: '10px',
                    padding: '14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    
                    {/* Include / Exclude Toggle */}
                    <div style={{ display: 'flex', gap: '4px', background: '#e2e8f0', padding: '3px', borderRadius: '6px' }}>
                      <button
                        type="button"
                        onClick={() => updateFilterRow(idx, 'mode', 'include')}
                        style={{
                          border: 'none',
                          background: flt.mode === 'include' ? '#22c55e' : 'transparent',
                          color: flt.mode === 'include' ? '#ffffff' : '#475569',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '700',
                          cursor: 'pointer',
                        }}
                      >
                        INCLUDE
                      </button>

                      <button
                        type="button"
                        onClick={() => updateFilterRow(idx, 'mode', 'exclude')}
                        style={{
                          border: 'none',
                          background: flt.mode === 'exclude' ? '#ef4444' : 'transparent',
                          color: flt.mode === 'exclude' ? '#ffffff' : '#475569',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '700',
                          cursor: 'pointer',
                        }}
                      >
                        EXCLUDE
                      </button>
                    </div>

                    {/* Filter Type Select */}
                    <select
                      value={flt.type}
                      onChange={(e) => updateFilterRow(idx, 'type', e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#0f172a',
                        flex: 1,
                        minWidth: '200px',
                      }}
                    >
                      {[
                        { value: 'order_count', label: 'Order Count (Min / Max)' },
                        { value: 'first_order_date', label: 'First Order Date' },
                        { value: 'last_order_date', label: 'Last Order Date / Days Ago' },
                        { value: 'ordered_skus', label: 'Ordered Product (SKU)' },
                        { value: 'ordered_category', label: 'Ordered Category' },
                        { value: 'warehouse', label: 'Hub / Warehouse' },
                        { value: 'last_active', label: 'Last Active on App' },
                        { value: 'signup_date', label: 'Signup Date' },
                        { value: 'specific_users', label: 'Specific Users (CSV / User IDs)' },
                        { value: 'audience', label: 'Another Audience (Include / Exclude)' },
                      ].map((opt) => (
                        <option
                          key={opt.value}
                          value={opt.value}
                          disabled={opt.value !== flt.type && filters.some((f, i) => i !== idx && f.type === opt.value)}
                        >
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={() => removeFilterRow(idx)}
                      style={{ border: 'none', background: 'none', color: '#ef4444', fontSize: '16px', cursor: 'pointer', padding: '0 4px' }}
                      title="Remove condition"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Filter Params Controls */}
                  <div style={{ fontSize: '13px', color: '#334155', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {flt.type === 'order_count' && (
                      <>
                        <span>Min Orders:</span>
                        <input
                          type="number"
                          value={flt.params.min ?? 0}
                          onChange={(e) => updateFilterRow(idx, 'params', { min: parseInt(e.target.value) })}
                          style={{ width: '80px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                        <span>Max Orders:</span>
                        <input
                          type="number"
                          placeholder="No max"
                          value={flt.params.max ?? ''}
                          onChange={(e) => updateFilterRow(idx, 'params', { max: e.target.value ? parseInt(e.target.value) : null })}
                          style={{ width: '90px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                      </>
                    )}

                    {flt.type === 'last_order_date' && (
                      <>
                        <span>More than</span>
                        <input
                          type="number"
                          placeholder="45"
                          value={flt.params.older_than_days ?? ''}
                          onChange={(e) => updateFilterRow(idx, 'params', { older_than_days: parseInt(e.target.value) || null })}
                          style={{ width: '80px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                        <span>days ago</span>
                      </>
                    )}

                    {flt.type === 'first_order_date' && (
                      <>
                        <span>From:</span>
                        <input
                          type="date"
                          value={flt.params.from || ''}
                          onChange={(e) => updateFilterRow(idx, 'params', { from: e.target.value || null })}
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                        <span>To:</span>
                        <input
                          type="date"
                          value={flt.params.to || ''}
                          onChange={(e) => updateFilterRow(idx, 'params', { to: e.target.value || null })}
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                      </>
                    )}

                    {flt.type === 'signup_date' && (
                      <>
                        <span>Signed up from:</span>
                        <input
                          type="date"
                          value={flt.params.from || ''}
                          onChange={(e) => updateFilterRow(idx, 'params', { from: e.target.value || null })}
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                        <span>to:</span>
                        <input
                          type="date"
                          value={flt.params.to || ''}
                          onChange={(e) => updateFilterRow(idx, 'params', { to: e.target.value || null })}
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                      </>
                    )}

                    {flt.type === 'ordered_skus' && (
                      <>
                        <span>SKUs (comma-separated, OR):</span>
                        <input
                          type="text"
                          placeholder="e.g. ZS046, ZS120"
                          value={(flt.params.values || []).join(', ')}
                          onChange={(e) => updateFilterRow(idx, 'params', { values: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                          style={{ flex: 1, minWidth: '220px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                      </>
                    )}

                    {flt.type === 'ordered_category' && (
                      <>
                        <span>Categories (select multiple):</span>
                        <select
                          multiple
                          value={flt.params.values || []}
                          onChange={(e) => updateFilterRow(idx, 'params', { values: Array.from(e.target.selectedOptions, (o) => o.value) })}
                          style={{ height: '70px', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e1', minWidth: '180px' }}
                        >
                          <option value="Solar Panels">Solar Panels</option>
                          <option value="Solar Inverter">Solar Inverter</option>
                          <option value="Wires & Cables">Wires & Cables</option>
                          <option value="Structure">Structure</option>
                        </select>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>(Hold Ctrl/Cmd to pick multiple — OR logic)</span>
                      </>
                    )}

                    {flt.type === 'warehouse' && (
                      <>
                        <span>Hubs (select multiple):</span>
                        <select
                          multiple
                          value={(flt.params.ids || []).map(String)}
                          onChange={(e) => updateFilterRow(idx, 'params', { ids: Array.from(e.target.selectedOptions, (o) => parseInt(o.value)) })}
                          style={{ height: '70px', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        >
                          {warehouses.map((wh) => (
                            <option key={wh.id} value={wh.id}>
                              {wh.name}
                            </option>
                          ))}
                        </select>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>(OR — any selected hub)</span>
                      </>
                    )}

                    {flt.type === 'last_active' && (
                      <>
                        <span>Active within last</span>
                        <input
                          type="number"
                          value={flt.params.within_days ?? 30}
                          onChange={(e) => updateFilterRow(idx, 'params', { within_days: parseInt(e.target.value) })}
                          style={{ width: '80px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                        <span>days</span>
                      </>
                    )}

                    {flt.type === 'specific_users' && (
                      <>
                        <span>
                          Includes {flt.params.values?.length || 0} specific user records uploaded.
                        </span>
                        <label
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#1d4ed8',
                            cursor: uploadLoading ? 'wait' : 'pointer',
                          }}
                        >
                          {uploadLoading ? '⏳ Uploading...' : '📁 Upload CSV/Excel'}
                          <input
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileUpload}
                            disabled={uploadLoading}
                            style={{ display: 'none' }}
                          />
                        </label>
                        {uploadResult && (
                          <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '600' }}>
                            ✓ {uploadResult.totalParsed} parsed, {uploadResult.matchedUsers} matched
                          </span>
                        )}
                      </>
                    )}

                    {flt.type === 'audience' && (
                      <>
                        <span>Audience ID:</span>
                        <input
                          type="text"
                          placeholder="AUD-104"
                          value={flt.params.audience_ids?.[0] || ''}
                          onChange={(e) => updateFilterRow(idx, 'params', { audience_ids: [e.target.value] })}
                          style={{ width: '120px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                      </>
                    )}
                  </div>
                </div>
                </React.Fragment>
              ))}
            </div>

            <button
              onClick={addFilterRow}
              className="btn btn-sm btn-outline-secondary"
              style={{ marginTop: '16px', borderRadius: '6px', fontWeight: '600', fontSize: '13px' }}
            >
              + Add Another Filter Row
            </button>
          </div>

          {/* Card 3: A/B Testing Split Config */}
          {type === 'static' && (
            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>3. A/B Testing Split (Treatment / Holdout)</span>
                  <span style={{ fontSize: '11px', background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '10px' }}>Deterministic Seed</span>
                </h4>

                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={enableSplit}
                    onChange={(e) => setEnableSplit(e.target.checked)}
                    id="enableSplitToggle"
                    style={{ cursor: 'pointer', width: '40px', height: '20px' }}
                  />
                  <label className="form-check-label" htmlFor="enableSplitToggle" style={{ fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginLeft: '6px' }}>
                    {enableSplit ? 'Split Enabled' : 'Disabled'}
                  </label>
                </div>
              </div>

              {enableSplit && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px' }}>
                  
                  {/* Presets */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Quick Presets:</span>
                    {[
                      { label: '80 / 20 (Standard)', val: 80 },
                      { label: '90 / 10', val: 90 },
                      { label: '50 / 50 (Equal Split)', val: 50 },
                    ].map((p) => (
                      <button
                        key={p.val}
                        onClick={() => setSplitPct(p.val)}
                        style={{
                          background: splitPct === p.val ? '#2563eb' : '#ffffff',
                          color: splitPct === p.val ? '#ffffff' : '#334155',
                          border: '1px solid #cbd5e1',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  {/* Slider */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '700', marginBottom: '8px' }}>
                      <span style={{ color: '#2563eb' }}>Treatment Group: {splitPct}%</span>
                      <span style={{ color: '#64748b' }}>Holdout Control Group: {100 - splitPct}%</span>
                    </div>

                    <input
                      type="range"
                      min="1"
                      max="99"
                      value={splitPct}
                      onChange={(e) => setSplitPct(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer' }}
                    />
                  </div>

                  {/* Group Preview Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ fontWeight: '700', color: '#1d4ed8', fontSize: '13px' }}>
                        Treatment Group (AUD-104T)
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: '800', color: '#1e40af', marginTop: '4px' }}>
                        {treatmentCount.toLocaleString()} <span style={{ fontSize: '12px', fontWeight: '500' }}>users ({splitPct}%)</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: '2px' }}>
                        Gets campaigns, coupons, and promocash.
                      </div>
                    </div>

                    <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ fontWeight: '700', color: '#475569', fontSize: '13px' }}>
                        Holdout Control Group (AUD-104H)
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: '800', color: '#334155', marginTop: '4px' }}>
                        {holdoutCount.toLocaleString()} <span style={{ fontSize: '12px', fontWeight: '500' }}>users ({100 - splitPct}%)</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                        Gets nothing — baseline for order rate comparison.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column — Live Debounced Preview Sidebar */}
        <div className="col-lg-4">
          <div style={{ position: 'sticky', top: '24px', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            
            {/* Header */}
            <div style={{ background: '#0f172a', padding: '16px 20px', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '12px', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.5px', fontWeight: '600' }}>Live Preview</div>
                <div style={{ fontSize: '22px', fontWeight: '800', marginTop: '2px' }}>
                  {previewLoading ? 'Evaluating...' : `${previewCount.toLocaleString()} Users`}
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '10px', background: previewLoading ? '#f59e0b' : '#22c55e', color: '#ffffff', padding: '3px 8px', borderRadius: '10px', fontWeight: '700' }}>
                  {previewLoading ? 'Calculating' : 'Live Synced'}
                </span>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '10px', textTransform: 'uppercase' }}>
                Sample Matching Users (10 Records)
              </div>

              <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>User</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>User ID</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleUsers.length === 0 ? (
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'center', padding: '16px', color: '#94a3b8' }}>
                          No matching samples
                        </td>
                      </tr>
                    ) : (
                      sampleUsers.map((usr) => (
                        <tr key={usr.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 8px', fontWeight: '600', color: '#0f172a' }}>{usr.name}</td>
                          <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#475569' }}>{usr.id}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '700', color: '#2563eb' }}>{usr.order_count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '12px', fontStyle: 'italic', textAlign: 'center' }}>
                Preview updates automatically on every filter change.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSV Modal */}
      {showCsvModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', width: '500px', maxWidth: '90%' }}>
            <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>
              Upload CSV or Paste User IDs
            </h4>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
              Paste user IDs (one per line):
            </p>

            <textarea
              rows="8"
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder="1045&#10;2087&#10;3312"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontFamily: 'monospace', outline: 'none' }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button
                onClick={() => setShowCsvModal(false)}
                className="btn btn-outline-secondary btn-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCsvSubmit}
                className="btn btn-primary btn-sm"
              >
                Add Users Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All-Users Confirmation Modal */}
      {showAllUsersModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '28px', width: '480px', maxWidth: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ background: '#fef2f2', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                ⚠️
              </div>
              <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                Target ALL Users?
              </h4>
            </div>
            <p style={{ fontSize: '14px', color: '#475569', marginBottom: '20px', lineHeight: '1.5' }}>
              This audience has <strong>no filters</strong> and will target <strong>ALL users</strong> in the system. This is a broad action and cannot be easily undone.
            </p>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>
              Are you sure you want to proceed?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => { setShowAllUsersModal(false); setPendingPayload(null); }}
                className="btn btn-outline-secondary"
                style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAllUsers}
                className="btn btn-danger"
                style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
              >
                Yes, Target All Users
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudienceBuilder;
